import { useEffect, useState } from "react";
import { ShoppingBag, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { money, fecha } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";

/** Ventas de la familia sin comprobante: se marcan para sumarlas a la factura. */
export const VentasSinFacturar = ({ tutorId, estudianteId, seleccion, onSeleccion,
                                    marcarPagada, onMarcarPagada }) => {
  const [ventas, setVentas] = useState([]);

  useEffect(() => {
    if (!tutorId && !estudianteId) return setVentas([]);
    api.get("/facturacion/ventas-pendientes", {
      params: tutorId ? { tutor_id: tutorId } : { estudiante_id: estudianteId },
    }).then((r) => setVentas(r.data)).catch(() => setVentas([]));
  }, [tutorId, estudianteId]);

  useEffect(() => { onSeleccion([]); }, [tutorId, estudianteId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (ventas.length === 0) return null;

  const marcadas = ventas.filter((v) => seleccion.includes(v.id));
  const total = marcadas.reduce((s, v) => s + Number(v.total || 0), 0);
  const alternar = (id, on) => onSeleccion(on ? [...seleccion, id]
                                              : seleccion.filter((x) => x !== id));

  return (
    <div className="space-y-3 rounded-md border border-border bg-muted/30 p-4"
         data-testid="ventas-sin-facturar">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
          <ShoppingBag className="h-4 w-4 text-primary" />
          Ventas sin facturar de esta familia ({ventas.length})
        </p>
        <button type="button" data-testid="marcar-todas-ventas"
                className="text-xs text-primary hover:underline"
                onClick={() => onSeleccion(marcadas.length === ventas.length
                  ? [] : ventas.map((v) => v.id))}>
          {marcadas.length === ventas.length ? "Quitar todas" : "Marcar todas"}
        </button>
      </div>

      {ventas.map((v) => (
        <label key={v.id} className="flex flex-wrap items-center gap-3 rounded-md bg-card p-3"
               data-testid={`venta-pendiente-${v.id}`}>
          <Checkbox checked={seleccion.includes(v.id)} data-testid={`marcar-venta-${v.id}`}
                    onCheckedChange={(on) => alternar(v.id, !!on)} />
          <span className="min-w-0 flex-1 text-sm">
            <span className="block truncate font-medium">{v.descripcion}</span>
            <span className="block truncate text-xs text-muted-foreground">
              {v.estudiante_nombre} · {fecha(v.fecha)} · {v.cantidad} unidad(es)
            </span>
          </span>
          <Badge variant="secondary" className="text-[10px]">
            cobrada · {v.metodo_pago || "efectivo"}
          </Badge>
          <span className="font-mono text-sm font-semibold">{money(v.total)}</span>
        </label>
      ))}

      {marcadas.length > 0 && (
        <>
          <p className="text-sm font-semibold" data-testid="total-ventas-marcadas">
            {marcadas.length} venta(s) por {money(total)} se agregarán a la factura
          </p>
          <label className="flex items-start gap-3 text-sm">
            <Checkbox checked={marcarPagada} data-testid="factura-marcar-pagada"
                      onCheckedChange={(v) => onMarcarPagada(!!v)} />
            <span>
              <span className="flex items-center gap-2 font-semibold">
                <Wallet className="h-4 w-4 text-primary" /> Registrar el cobro de estas ventas
              </span>
              <span className="block text-xs text-muted-foreground">
                Se acredita {money(total)} a la factura con el método de cada venta y en la fecha
                en que se cobró, para no duplicar el ingreso en el cierre de caja.
              </span>
            </span>
          </label>
        </>
      )}
    </div>
  );
};
